import type { Middleware } from "@reduxjs/toolkit"
import type { BaseQueryFn } from "@reduxjs/toolkit/query/react"
import { createApi, fetchBaseQuery, retry } from "@reduxjs/toolkit/query/react"
import queryString from "query-string"

import type {
  OrgsReq,
  OrgsRes,
  UpdateOrgAuthMechanismsReq,
  UpdateOrgNameReq,
  UpdateOrgSettingsReq,
} from "types/orgs.types"
import type { DeviceTelemetryReq, DeviceTelemetryRes } from "types/telemetries.types"
import type { QualityRecord } from "types/quality.types"
import type {
  RemoveOrgUserReq,
  OrgUsersReq,
  OrgUsersRes,
  UpdateOrgUserReq,
  UpdateOrgUserRes,
  UsersLogsReq,
  UsersLogsRes,
} from "types/users.types"
import type {
  DeleteLocationReq,
  GeoReq,
  GeoRes,
  LastLocationsReq,
  LastLocationsRes,
  UpdateLocationReq,
} from "types/geolocation.types"
import type { Paged } from "types/common.types"
import { API_URL } from "../config"
import { invalidateCredentials } from "../store/authSlice"
import type { RootState } from "../store"
import { isSuperseded, withRequestQueue } from "./requestQueue"

const baseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  // `fetch` waits forever on a connection that dies without closing — wifi to
  // data mid load, the VPN dropping — and a request that never settles holds its
  // queue slot for the life of the tab. Well above any answer the API gives:
  // its own Influx timeout is ten seconds, and this only has to catch the dead.
  timeout: 60_000,
  prepareHeaders: (headers, { getState = () => ({}) }) => {
    const state = getState() as RootState
    const token = state?.auth?.token
    const newHeaders = new Headers(headers)
    if (token) {
      newHeaders.set("authorization", `Bearer ${token}`)
    }
    return newHeaders
  },
  paramsSerializer: queryString.stringify,
})
const baseQueryWithTokenInvalidation: BaseQueryFn = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions)
  // Only invalidate credentials when an authenticated request
  const hadToken = Boolean((api.getState() as RootState)?.auth?.token)
  if (hadToken && result.error && result.error.status === 401) {
    // Invalidate credentials
    api.dispatch(invalidateCredentials())
  }
  return result
}

const baseQueryWithRetry = retry(withRequestQueue(baseQueryWithTokenInvalidation), {
  retryCondition: (error, args, { attempt, baseQueryApi }) => {
    // Stop after 3 retries
    if (attempt > 3) {
      return false
    }
    // Nothing is waiting for the answer, so a request the queue dropped as
    // aborted would otherwise go straight back in line.
    if (baseQueryApi.signal.aborted) {
      return false
    }
    // A superseded request carries no aborted signal — the range moved on, the
    // subscription did not — so it has to be recognised on its own or the queue
    // would drop it and the retry would put it straight back.
    if (isSuperseded(error)) {
      return false
    }
    // Only retry safe/idempotent methods — retrying mutations (POST/PATCH/DELETE)
    // could duplicate side effects. Queries pass no method and default to GET.
    const method =
      typeof args === "object" && args !== null ? (args.method ?? "GET") : "GET"
    if (!["GET", "HEAD"].includes(method.toUpperCase())) {
      return false
    }
    const status = (error as { status?: unknown })?.status
    // The API reports an overloaded store and a malformed query with the same
    // 400, so this one 4xx is worth another go — once, and no further, since a
    // malformed query is our own bug and would fail every time.
    const code = (error as { data?: { code?: unknown } })?.data?.code
    if (status === 400 && code === "QUERY_ERROR") {
      return attempt === 1
    }
    // Do not retry client errors (4xx) — they are deterministic
    if (typeof status === "number" && status >= 400 && status < 500) {
      return false
    }
    return true
  },
  backoff: async (attempt: number) => {
    // Jittered: a wave of failures that retried in lockstep would hit the API
    // in even bursts while it is already struggling.
    const delay = Math.pow(2, attempt - 1) * 1000 * (0.5 + Math.random())
    await new Promise<void>((resolve) => setTimeout(resolve, delay))
  },
})

export const api = createApi({
  baseQuery: baseQueryWithRetry,
  tagTypes: [
    "ERROR",
    "UNAUTHORIZED",
    "Alert",
    "AlertSetting",
    "AlertSettingState",
    "Conductor",
    "Device",
    "DeviceGroup",
    "DeviceId",
    "DeviceLocation",
    "EditableFeatures",
    "Organization",
    "OrgFeaturesLocation",
    "OrgUsersLogs",
    "Powerline",
    "Span",
    "Tower",
    "User",
    "UserTerms",
  ],
  endpoints: (builder) => ({
    geo: builder.query<GeoRes, GeoReq>({
      query: ({ orgId }) => ({
        url: `/v1/orgs/${encodeURIComponent(orgId)}/geo`,
      }),
    }),
    lastLocations: builder.query<LastLocationsRes, LastLocationsReq>({
      query: ({ params }) => ({
        url: "/v1/gps_locations",
        params,
      }),
      providesTags: (_result, _error, { params }) => {
        const ids = (params?.less_ids || []).slice()
        if (params?.less_id) {
          ids.push(params.less_id)
        }
        return ids.map((id) => ({ type: "DeviceLocation" as const, id }))
      },
    }),
    updateLocation: builder.mutation<null, UpdateLocationReq>({
      query: ({ deviceId, latitude, longitude }) => ({
        url: "/v1/gps_locations",
        method: "POST",
        body: { less_id: deviceId, latitude, longitude },
      }),
      invalidatesTags: (_result, _error, { deviceId: id }) => [
        { type: "DeviceLocation" as const, id },
      ],
    }),
    deleteLocation: builder.mutation<null, DeleteLocationReq>({
      query: ({ deviceId }) => ({
        url: "/v1/gps_locations",
        method: "DELETE",
        body: { less_id: deviceId },
      }),
      invalidatesTags: (_result, _error, { deviceId: id }) => [
        { type: "DeviceLocation" as const, id },
      ],
    }),
    deviceTelemetry: builder.query<DeviceTelemetryRes, DeviceTelemetryReq>({
      query: ({ id, params }) => ({
        url: `/v1/devices/${encodeURIComponent(id)}/telemetry`,
        params: {
          ...params,
          format: "json",
        },
      }),
    }),
    deviceTelemetryQuality: builder.query<QualityRecord[], DeviceTelemetryReq>({
      query: ({ id, params }) => ({
        url: `/v1/devices/${encodeURIComponent(id)}/telemetry`,
        params: {
          ...params,
          format: "json",
          only_quality_summary: true,
        },
      }),
    }),
    orgs: builder.query<OrgsRes, OrgsReq>({
      query: () => ({
        url: "/v1/orgs",
        params: { extras: ["roles"] },
      }),
      providesTags: (result, _error, { user = null } = {}) => {
        return result
          ? [
              ...result.orgs.map(({ id }) => ({ type: "Organization" as const, id })),
              { type: "Organization", id: `User(${user})` },
            ]
          : [{ type: "Organization", id: `User(${user})` }]
      },
    }),
    orgUsers: builder.query<OrgUsersRes, OrgUsersReq>({
      query: ({ orgId }) => ({
        url: `/v1/orgs/${orgId}/users`,
      }),
      providesTags: (result, _error, { orgId }) => {
        return result
          ? [
              ...result.users.map(({ username }) => ({
                type: "User" as const,
                id: username,
              })),
              { type: "User", id: `Organization(${orgId})` },
            ]
          : [{ type: "User", id: `Organization(${orgId})` }]
      },
    }),
    removeOrgUser: builder.mutation<null, RemoveOrgUserReq>({
      query: ({ orgId, username }) => ({
        url: `/v1/orgs/${orgId}/users/${encodeURIComponent(username)}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: "User", id: `Organization(${orgId})` },
      ],
    }),
    updateOrgUser: builder.mutation<UpdateOrgUserRes, UpdateOrgUserReq>({
      query: ({ orgId, username, role }) => ({
        url: `/v1/orgs/${orgId}/users/${encodeURIComponent(username)}`,
        method: "PATCH",
        body: {
          role,
        },
      }),
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: "User", id: `Organization(${orgId})` },
      ],
    }),
    updateOrgName: builder.mutation<null, UpdateOrgNameReq>({
      query: ({ name, orgId }) => ({
        url: `/v1/orgs/${encodeURIComponent(orgId)}`,
        method: "PATCH",
        body: { name },
      }),
      invalidatesTags: (_result, error, { orgId: id }) => {
        return !error ? [{ type: "Organization", id }] : []
      },
    }),
    updateOrgSettings: builder.mutation<null, UpdateOrgSettingsReq>({
      query: ({ emails, language, orgId }) => ({
        url: `/v1/orgs/${encodeURIComponent(orgId)}`,
        method: "PATCH",
        body: { emails, language },
      }),
      invalidatesTags: (_result, error, { orgId: id }) => {
        return !error ? [{ type: "Organization", id }] : []
      },
    }),
    updateOrgAuthMechanisms: builder.mutation<null, UpdateOrgAuthMechanismsReq>({
      query: ({ auth_mechanisms, orgId }) => ({
        url: `/v1/orgs/${encodeURIComponent(orgId)}`,
        method: "PATCH",
        body: { auth_mechanisms },
      }),
      invalidatesTags: (_result, error, { orgId: id }) => {
        return !error ? [{ type: "Organization", id }] : []
      },
    }),
    orgUsersLogs: builder.query<Paged<UsersLogsRes>, UsersLogsReq>({
      query: ({ org_id, from_date, to_date, cursor, limit }) => ({
        url: `/v1/orgs/${org_id}/users_logs`,
        params: { from_date, to_date, cursor, limit },
      }),
      providesTags: (_result, _error, { org_id }) => [
        { type: "OrgUsersLogs", id: `Organization(${org_id})` },
      ],
    }),
  }),
})

export const apiMiddleware: Middleware = (store) => (next) => (action) => {
  const result = next(action)
  if (typeof action === "object" && action !== null && "type" in action) {
    switch (action.type) {
      case "auth/invalidateCredentials": {
        store.dispatch(api.util.resetApiState())
        break
      }
      default:
        break
    }
  }
  return result
}
